import React from 'react';
import {
  AddOperandButton,
  Filter,
  getEmptyFilterExpression,
  getRootOperator,
  OperatorType,
  RelationsProps,
  StringConditionType,
} from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { emptyObject } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { ExpressionBuilder, useExpressionBuilder } from './ExpressionBuilderContext';
import { FilterConfig, useFilterConfig } from './FilterContext';
import { useExpressionOperations } from './hooks/useExpressionOperations';
import RenderOperator from './RenderOperator';

export type ExpressionBuilderProps = NewExpressionBuilderBaseProps &
  ExpressionBuilder &
  Omit<FilterConfig, 'size'> & {
    size?: 'xSmall' | 'small' | 'medium';
    fieldSelectionLimit?: { limit: number; message: string };
  };

export interface NewExpressionBuilderBaseProps {
  filter?: Filter;
  setFilter?: any;
  rowsLimit?: { limit: number; message: string };
}

export const NewExpressionBuilderBase: React.FC<NewExpressionBuilderBaseProps> = ({ setFilter, filter: filterProp, rowsLimit }) => {
  const {
    supportParser = false,
    defaultField = null,
    relationsProps = emptyObject as RelationsProps,
    hasListTab = false,
    readOnly = false,
    disableMessage,
    canDeleteLast,
  } = useExpressionBuilder();
  const filter = filterProp ?? getEmptyFilterExpression(null, supportParser ? StringConditionType.EQUALS : undefined);
  const { size, isVertical, fullWidth } = useFilterConfig();
  const rootOperator = getRootOperator<OperatorType.AND | OperatorType.OR>(filter);
  const oppositeOperator = rootOperator === OperatorType.OR ? OperatorType.AND : OperatorType.OR;
  const { addGlobal, disableAddButton, toggleOperators } = useExpressionOperations(filter, setFilter, {
    canDeleteLast,
    defaultEmptyFilter: relationsProps.defaultEmptyFilter,
    defaultField,
    hasListTab,
    rootOperator,
    rowsLimit,
    supportParser,
  });

  const onToggleLogicalOperators = readOnly ? () => {} : toggleOperators;

  return (
    <Box sx={{ ...flex.col, ...(fullWidth && !isVertical && { width: '100%' }) }}>
      <RenderOperator
        rootOperator={rootOperator}
        filter={filter}
        setFilter={setFilter}
        onToggleLogicalOperators={onToggleLogicalOperators}
      />
      {rowsLimit?.limit !== 1 && !readOnly && (
        <Box sx={{ ...flex.row, gap: 1, my: 1 }}>
          <AddOperandButton
            size={size}
            operator={rootOperator}
            isAdd
            onClick={() => addGlobal(rootOperator)}
            disabled={disableAddButton}
            disableMessage={disableMessage}
          />
          {filter[rootOperator]?.operands.length === 1 && (
            <AddOperandButton
              size={size}
              operator={oppositeOperator}
              isAdd
              onClick={() => addGlobal(oppositeOperator)}
              disabled={disableAddButton}
              disableMessage={disableMessage}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
